import type { PnidWizardState } from '../types';
import { normalizeAssetTag } from './normalizeAssetTag';

/** One selected canvas annotation, as the workspace addresses it to the backend. */
export interface SelectedAnnotation {
  kind: 'equipment' | 'instrument' | 'node' | 'controlLoop' | 'relationship';
  id: string;
}

/** What a selection of equipment offers a redundancy set. */
export interface RedundancySetSelection {
  /** The tags of the selected tagged equipment, as stored. */
  tags: string[];
  /** A name for every selected equipment that carries no tag, and so cannot be a member. */
  untaggedItems: string[];
  /** How many different tags `tags` holds once separators are normalized. */
  distinctTagCount: number;
}

/**
 * The ids of a selection that holds equipment alone, or null when it holds anything else.
 *
 * DECISION(2026-10-26, abhishek): The canvas offers "Mark as redundancy set" only when
 * every selected element is equipment.
 * The SME groups primary and spare equipment into redundancy sets and asked that
 * instruments join none, so neither the canvas nor the instrument editor offers one.
 */
export function equipmentOnlySelectionIds(selection: SelectedAnnotation[]): string[] | null {
  if (selection.length === 0 || selection.some((item) => item.kind !== 'equipment')) return null;
  return selection.map((item) => item.id);
}

/**
 * Only a tag is a plant-wide identity, so an equipment with no tag cannot join a
 * redundancy set.
 */
export function resolveRedundancySetSelection(
  selectedEquipmentIds: string[],
  state: Pick<PnidWizardState, 'equipments'>,
): RedundancySetSelection {
  const tags: string[] = [];
  const untaggedItems: string[] = [];

  for (const equipmentId of selectedEquipmentIds) {
    const equipment = state.equipments.find((candidate) => candidate.id === equipmentId);
    const tag = equipment?.equipment_no?.trim();
    if (tag) tags.push(tag);
    else untaggedItems.push(equipment?.equipment_type || 'an equipment with no tag');
  }

  return {
    tags,
    untaggedItems,
    distinctTagCount: new Set(tags.map(normalizeAssetTag)).size,
  };
}
